/**
 * One list in a group's consumption queue — sortable only in MANUAL mode.
 * 02/11/2026 (user: "really messy and not readable"): the formula's score
 * and unique visits are right-aligned columns (or why the list is
 * unranked), and the row today's drops use says "Today", the list the next
 * generate starts on "Next" — "first in line" on #1 hid both.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';

import { Tooltip } from '@/components/ui';
import { cn } from '@/lib/utils';

import type { FormulaCells, QueueMark } from './formula/queue-order';
import { TagChips } from '../rules/TagChips';

const MARKS: Record<QueueMark, { label: string; hint: string; className: string }> = {
  today: { label: 'Today', hint: "Today's drops go to this list.", className: 'bg-primary/10 text-primary' },
  next: { label: 'Next', hint: "Generate now and the next auto-generate start on this list — the formula's best that isn't burned.", className: 'border border-primary/40 text-primary' },
};

/** The columns' widths — an unranked row's reason sits where score and visits go;
 *  on a phone a ranked row keeps only its score, so the name keeps its room. */
export const CELLS_WIDTH = { score: 'w-14 sm:w-16', visits: 'hidden w-24 sm:block', reason: 'max-w-[10rem] sm:max-w-[20rem]', leads: 'hidden w-24 sm:block' } as const;

interface Props {
  wiserId: number;
  rank: number;
  name: string;
  leadCount: number | null;
  draggable: boolean;
  /** Phase 18 — the list formula's columns (score, visits, or why unranked). */
  cells?: FormulaCells;
  mark?: QueueMark;
  /** Some row knows its lead count: every row keeps the column. */
  leadsColumn?: boolean;
  /** Phase 25 — its list tags, when a rule tests one. */
  tags?: string[];
}

export const OrderQueueRow: FC<Props> = ({ wiserId, rank, name, leadCount, draggable, cells, mark, leadsColumn, tags }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: String(wiserId), disabled: !draggable });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn('flex items-center gap-2 rounded-md px-2 py-1 text-xs', isDragging && 'z-10 bg-background shadow-md', mark === 'today' && 'bg-primary/5')}
    >
      {draggable ? (
        <button type="button" aria-label={`Reorder ${name}`} className="cursor-grab text-muted-foreground/70 hover:text-foreground" {...attributes} {...listeners}>
          <GripVertical className="h-3.5 w-3.5" />
        </button>
      ) : (
        <span className="hidden w-3.5 shrink-0 sm:block" />
      )}
      <span className="w-6 shrink-0 text-right font-mono tabular-nums text-muted-foreground">{rank}</span>
      <span className="min-w-[5rem] truncate font-medium" title={name}>{name}</span>
      {mark ? <MarkChip mark={mark} /> : null}
      <TagChips tags={tags} />
      <span className="grow" />
      {cells ? <Cells cells={cells} /> : null}
      {/* Unknown lead count: nothing — a lone dash on every row read as noise. */}
      {leadsColumn || leadCount != null ? (
        <span className={cn('shrink-0 text-right font-mono text-[11px] tabular-nums text-muted-foreground', cells ? CELLS_WIDTH.leads : 'w-24')}>
          {leadCount != null ? `${leadCount.toLocaleString('en-US')} leads` : ''}
        </span>
      ) : null}
    </div>
  );
};

const MarkChip: FC<{ mark: QueueMark }> = ({ mark }) => (
  <Tooltip content={MARKS[mark].hint}>
    <span className={cn('shrink-0 rounded-full px-2 py-px text-[10px] font-semibold uppercase tracking-wider', MARKS[mark].className)}>{MARKS[mark].label}</span>
  </Tooltip>
);

const Cells: FC<{ cells: FormulaCells }> = ({ cells }) =>
  cells.reason !== null ? (
    <span className={cn('min-w-0 truncate text-right text-[11px] text-muted-foreground', CELLS_WIDTH.reason)} title={cells.reason}>
      {cells.reason}
    </span>
  ) : (
    <>
      <span className={cn('shrink-0 text-right font-mono text-[11px] tabular-nums text-foreground', CELLS_WIDTH.score)}>{cells.score}</span>
      <span className={cn('shrink-0 text-right font-mono text-[11px] tabular-nums text-muted-foreground', CELLS_WIDTH.visits)}>{cells.visits}</span>
    </>
  );
